import {
  AloftArea,
  AloftPrice,
  LocationType,
  ManualAdjustmentKey,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftArea,
  schAloftPrice,
  schLocationType,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import { CompAdjustments } from '../../../../redux/features/selectors/CompAdjustments.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import {
  isConditionAdjustmentMaterial,
  isGlaAdjustmentMaterial,
  isSiteSizeAdjustmentMaterial,
} from '../utils/compAdjustmentMateriality.ts';
import { getComparableDisplayName, getScopedAlertType } from '../utils/compTypeScoping.ts';

type FeatureComparison = 'greater' | 'lesser';

type FeatureCheck = {
  label: string;
  adjustmentCellKeyId: ManualAdjustmentKey;
  isMaterialDifference: (params: {
    subjectValue: number | AloftArea;
    compValue: number | AloftArea;
    locationType?: LocationType;
  }) => boolean;
  getWrongDirectionComparison: (
    adjustment: AloftPrice,
    compValue: number | AloftArea,
    subjectValue: number | AloftArea,
  ) => FeatureComparison | undefined;
  getSubjectValue: (
    subject: SubjectPropertyDataState['subjectData']['editableData'],
  ) => number | AloftArea | undefined;
  getCompValue: (
    comp: CompPropertyDataState['compData']['editableData'],
  ) => number | AloftArea | undefined;
  getAdjustment: (compAdjustments: CompAdjustments) => AloftPrice | undefined;
};

const glaFeatureCheck: FeatureCheck = {
  label: 'GLA',
  adjustmentCellKeyId: 'gla',
  isMaterialDifference: ({ subjectValue, compValue }) =>
    isGlaAdjustmentMaterial(subjectValue as AloftArea, compValue as AloftArea),
  getSubjectValue: (subject) => orUndefined(schAloftArea.parse)(subject.improvements?.gla),
  getCompValue: (comp) => orUndefined(schAloftArea.parse)(comp.improvements?.gla),
  getAdjustment: (compAdjustments) =>
    orUndefined(schAloftPrice.parse)(compAdjustments.mainDwelling?.gla?.adjustment),
  getWrongDirectionComparison: (adjustment, compValue, subjectValue) => {
    const compGla = compValue as AloftArea;
    const subjectGla = subjectValue as AloftArea;

    if (adjustment.gt(AloftPrice.zero) && compGla.gt(subjectGla)) return 'greater';
    if (adjustment.lt(AloftPrice.zero) && compGla.lt(subjectGla)) return 'lesser';
    return undefined;
  },
};

const siteSizeFeatureCheck: FeatureCheck = {
  label: 'Site Size',
  adjustmentCellKeyId: 'site',
  isMaterialDifference: ({ subjectValue, compValue, locationType }) =>
    isSiteSizeAdjustmentMaterial(subjectValue as AloftArea, compValue as AloftArea, locationType),
  getSubjectValue: (subject) => orUndefined(schAloftArea.parse)(subject.site?.area),
  getCompValue: (comp) => orUndefined(schAloftArea.parse)(comp.site?.area),
  getAdjustment: (compAdjustments) =>
    orUndefined(schAloftPrice.parse)(compAdjustments.site?.size?.adjustment),
  getWrongDirectionComparison: (adjustment, compValue, subjectValue) => {
    const compArea = compValue as AloftArea;
    const subjectArea = subjectValue as AloftArea;

    if (adjustment.gt(AloftPrice.zero) && compArea.ge(subjectArea)) return 'greater';
    if (adjustment.lt(AloftPrice.zero) && compArea.le(subjectArea)) return 'lesser';
    return undefined;
  },
};

const conditionFeatureCheck: FeatureCheck = {
  label: 'Condition',
  adjustmentCellKeyId: 'condition',
  isMaterialDifference: ({ subjectValue, compValue }) =>
    isConditionAdjustmentMaterial(subjectValue as number, compValue as number),
  getSubjectValue: (subject) => {
    const value = subject.improvements?.uadCondition;
    return isDefined(value) ? value : undefined;
  },
  getCompValue: (comp) => {
    const value = comp.improvements?.uadCondition;
    return isDefined(value) ? value : undefined;
  },
  getAdjustment: (compAdjustments) =>
    orUndefined(schAloftPrice.parse)(compAdjustments.condition?.adjustment),
  getWrongDirectionComparison: (adjustment, compValue, subjectValue) => {
    const compCondition = compValue as number;
    const subjectCondition = subjectValue as number;

    if (adjustment.gt(AloftPrice.zero) && compCondition < subjectCondition) return 'lesser';
    if (adjustment.lt(AloftPrice.zero) && compCondition > subjectCondition) return 'greater';
    return undefined;
  },
};

const FEATURE_CHECKS: FeatureCheck[] = [
  glaFeatureCheck,
  siteSizeFeatureCheck,
  conditionFeatureCheck,
];

const COMPARISON_MESSAGES: Record<FeatureComparison, { message: string; tips: string }> = {
  greater: {
    message: 'greater',
    tips: 'greater',
  },
  lesser: {
    message: 'lesser',
    tips: 'lesser',
  },
};

const EXPECTED_ADJUSTMENT_DIRECTION: Record<FeatureComparison, string> = {
  greater: 'negative',
  lesser: 'positive',
};

const TARGET_CELL_TOOLTIP = 'Adjustment is in the wrong direction';

export const inconsistentCompAdjustment: AlertDefinition = ({
  compAdjustments,
  compSequence,
  compType,
  order,
  propertyDataEntities,
}) => {
  const subjectData = (propertyDataEntities.subject as SubjectPropertyDataState | undefined)
    ?.subjectData.editableData;

  if (!subjectData) return undefined;

  const locationType = orUndefined(schLocationType.parse)(
    subjectData.propertyIdentification?.locationType,
  );

  const faultyAdjustment = compSequence
    .flatMap((id) => {
      const propertyData = (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData
        .editableData;
      const compAdjustmentData = compAdjustments[id];
      if (!propertyData || !compAdjustmentData) return [];

      return FEATURE_CHECKS.flatMap((featureCheck) => {
        const subjectValue = featureCheck.getSubjectValue(subjectData);
        const compValue = featureCheck.getCompValue(propertyData);
        const adjustment = featureCheck.getAdjustment(compAdjustmentData);

        if (
          !isDefined(subjectValue) ||
          !isDefined(compValue) ||
          !isDefined(adjustment) ||
          adjustment.equals(AloftPrice.zero) ||
          !featureCheck.isMaterialDifference({ subjectValue, compValue, locationType })
        ) {
          return [];
        }

        const comparison = featureCheck.getWrongDirectionComparison(
          adjustment,
          compValue,
          subjectValue,
        );
        if (!comparison) return [];

        return [{ id, featureCheck, comparison, adjustment }];
      });
    })
    .at(0);

  if (!faultyAdjustment) return undefined;

  const { id, featureCheck, comparison, adjustment } = faultyAdjustment;
  const propertyName = getComparableDisplayName(id, compSequence);
  const comparisonMessage = COMPARISON_MESSAGES[comparison];
  const adjustmentDirection = adjustment.gt(AloftPrice.zero) ? 'positive' : 'negative';
  const targetCellKey = `${id}-${featureCheck.adjustmentCellKeyId}` as PropertyGridCellKey;

  const fixRoute =
    schPairedCostRouteItem.enum[order.isInspectifyOrder ? 'adjustments-grid' : 'adjustments'];
  const triggers = getAlertTriggers({ fixRoute, order, includeUpdate: true });

  return {
    type: getScopedAlertType(schAlertType.enum.inconsistentCompAdjustment, compType),
    status: 'active',
    severity: 'error',
    orderType: ['appraisify'],
    message: `${propertyName} has a ${comparisonMessage.message} ${featureCheck.label} than the Subject but a ${adjustmentDirection} adjustment is shown.`,
    propertyID: id,
    ...(compType ? { compType } : {}),
    fixingInfo: {
      tips: `Since the Comp is ${comparisonMessage.tips} in ${featureCheck.label}, a ${EXPECTED_ADJUSTMENT_DIRECTION[comparison]} adjustment is needed to normalize its value to the Subject. Alternatively, explain why the adjustment is justified.`,
      pairedCostRouteItem: fixRoute,
      fixingMethod: ['comment', 'action'],
      commentAlwaysRequired: false,
      targetCellKey,
      targetCellTooltip: TARGET_CELL_TOOLTIP,
    },
    triggers,
  };
};
